// Per-project CRM member assignments. Endpoint: /api/v1/crm/projects/:id/members.
// CRM-scoped — TG host's Project model doesn't know about these.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { crmApi } from "../api/client";

const key = (projectId) => ["crm", "project-members", projectId];

export function useProjectMembers(projectId) {
  return useQuery({
    queryKey: key(projectId),
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await crmApi.get(`/projects/${projectId}/members`);
      return data?.members || [];
    },
    staleTime: 30_000,
  });
}

// Project membership changes ripple to:
//   - the projects list (which a non-admin only sees if assigned)
//   - leads list (visibility scoping for newly-added/removed users)
//   - dashboard (per-user metrics)
// We patch the local members cache first for instant UI, then invalidate
// outward so dependent queries refresh.
function syncProjectMembers(qc, projectId, members) {
  qc.setQueryData(key(projectId), members);
  qc.invalidateQueries({ queryKey: ["crm", "projects"] });
  qc.invalidateQueries({ queryKey: ["crm", "leads"] });
  qc.invalidateQueries({ queryKey: ["crm", "dashboard", "overview"] });
}

export function useSetProjectMembers(projectId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userIds) => {
      const { data } = await crmApi.put(`/projects/${projectId}/members`, { userIds });
      return data?.members || [];
    },
    onSuccess: (members) => syncProjectMembers(qc, projectId, members),
  });
}

export function useAddProjectMember(projectId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId) => {
      const { data } = await crmApi.post(`/projects/${projectId}/members`, { userId });
      return data?.members || [];
    },
    onSuccess: (members) => syncProjectMembers(qc, projectId, members),
  });
}

export function useRemoveProjectMember(projectId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId) => {
      const { data } = await crmApi.delete(`/projects/${projectId}/members/${userId}`);
      return data?.members || [];
    },
    onSuccess: (members) => syncProjectMembers(qc, projectId, members),
  });
}
